import { Text } from '@thanx/uikit/text'
import type { PhilosophyPosture } from 'api/recoveryAi.types'
import { buildTranslate } from 'locales'
import React from 'react'
import RadioOptionCard from 'scenes/RecoveryAI/components/RadioOptionCard'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import { useWizardState } from '../WizardContext'

const t = buildTranslate('recovery_ai.wizard.assessment')
// R12 renames this stage to "Train your agent" (the scoring is unchanged). The
// legacy philosophy.* keys stay (the Configuration dashboard still reads them);
// the wizard stage now reads its own train.* copy.
const tTrain = buildTranslate('recovery_ai.wizard.train')

type Question = {
  badgeKey: string
  questionKey: string
  optionKeys: string[]
}

const QUESTIONS: Question[] = [
  {
    badgeKey: 'q1_badge',
    questionKey: 'q1_question',
    optionKeys: ['q1_option_1', 'q1_option_2', 'q1_option_3', 'q1_option_4'],
  },
  {
    badgeKey: 'q2_badge',
    questionKey: 'q2_question',
    optionKeys: ['q2_option_1', 'q2_option_2', 'q2_option_3', 'q2_option_4'],
  },
  {
    badgeKey: 'q3_badge',
    questionKey: 'q3_question',
    optionKeys: ['q3_option_1', 'q3_option_2', 'q3_option_3', 'q3_option_4'],
  },
]

function computeRecommendedPosture(
  answers: Record<number, number>
): PhilosophyPosture {
  const values = Object.values(answers)
  if (values.length === 0) return 'generous'
  const avg = values.reduce((sum, v) => sum + v, 0) / values.length
  if (avg <= 0.75) return 'frugal'
  if (avg <= 1.5) return 'balanced'
  if (avg <= 2.25) return 'generous'
  return 'whatever_it_takes'
}

// The Train stage is complete only once every question is answered (a posture
// has been computed). Exported so the wizard gates the Train Continue button on
// it.
function isAssessmentComplete(answers: Record<number, number>): boolean {
  return Object.keys(answers).length === QUESTIONS.length
}

const PhilosophyAssessment = () => {
  const [css] = useStyletron()
  const { state, update } = useWizardState()
  const answers = state.assessmentAnswers

  const handleSelect = (questionIndex: number, optionIndex: number) => {
    const newAnswers = { ...answers, [questionIndex]: optionIndex }
    const recommendedPosture = isAssessmentComplete(newAnswers)
      ? computeRecommendedPosture(newAnswers)
      : null
    update({
      assessmentAnswers: newAnswers,
      recommendedPosture,
      // R12 unwired the RecoveryPhilosophy/useRecommendedPosture step that used
      // to copy recommendedPosture -> philosophyPosture (see WizardContext),
      // and Train is the only live posture input today (no override UI is
      // mounted), so without this the config PUT — and the Settings generosity
      // slider that hydrates from it — stay stuck at WizardContext's default
      // ('generous') no matter what the merchant answers.
      ...(recommendedPosture !== null && {
        philosophyPosture: recommendedPosture,
      }),
    })
  }

  return (
    <div
      className={css({
        maxWidth: '1108px',
        margin: '0 auto',
      })}
    >
      <div className={`d-flex flex-column ${css({ gap: '16px' })}`}>
        {/* Title */}
        <Text.Header3 bold>{tTrain('title')}</Text.Header3>

        {/* Subtitle */}
        <Text.BodyText3 color="grey70">{tTrain('description')}</Text.BodyText3>

        {/* Bold instruction */}
        <Text.BodyText3 bold>{t('title_bold')}</Text.BodyText3>

        {/* Info banner */}
        <div
          className={`d-flex align-items-center ${css({
            backgroundColor: merchantTheme.colors.belizeHole10,
            borderRadius: '4px',
            padding: '12px 16px',
            gap: '12px',
          })}`}
        >
          <i
            className={`fa fa-info-circle ${css({
              color: merchantTheme.colors.belizeHole50,
              fontSize: '16px',
              flexShrink: 0,
            })}`}
            aria-hidden="true"
          />
          <Text.BodyText4 color="grey70">
            {tTrain('info_banner')}
          </Text.BodyText4>
        </div>

        {/* All assessment questions are shown together as a column of
            selectable areas — one radio group per question — rather than a
            paginated one-question-at-a-time wizard. */}
        {QUESTIONS.map((question, questionIndex) => (
          <div
            key={question.questionKey}
            className={`d-flex flex-column ${css({
              gap: '12px',
              marginTop: '16px',
            })}`}
          >
            {/* Badge */}
            <div>
              <span
                className={css({
                  display: 'inline-block',
                  backgroundColor: merchantTheme.colors.belizeHole10,
                  color: merchantTheme.colors.belizeHole50,
                  fontWeight: 700,
                  fontSize: '12px',
                  lineHeight: '16px',
                  padding: '2px 8px',
                  borderRadius: '4px',
                })}
              >
                {t(question.badgeKey)}
              </span>
            </div>

            {/* Question text */}
            <Text.BodyText2 bold>{t(question.questionKey)}</Text.BodyText2>

            {/* Radio options */}
            <div className={`d-flex flex-column ${css({ gap: '12px' })}`}>
              {question.optionKeys.map((optionKey, optionIndex) => (
                <RadioOptionCard
                  key={optionKey}
                  selected={answers[questionIndex] === optionIndex}
                  value={String(optionIndex)}
                  name={`assessment-q${questionIndex}`}
                  onChange={() => handleSelect(questionIndex, optionIndex)}
                  ariaLabel={t(optionKey)}
                >
                  <Text.BodyText4>{t(optionKey)}</Text.BodyText4>
                </RadioOptionCard>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default PhilosophyAssessment
export { QUESTIONS, computeRecommendedPosture, isAssessmentComplete }
